import httpServiceAuth from '@/services/http-service';
import { apiEndpoints } from '@/services/constants';
import { errorHandler } from '@/services/error-handler';
import store from '@/store';

export default {
  state: {
    editHelpPage: null,
    helpPages: [],
    nameTranslations: [],
    contentTranslations: [],
    selectedLanguage: null,
    redirectRoute: null,
    css: null,
    defaultNames: [
      { locale: 'en', name: 'Help' },
      { locale: 'en-gb', name: 'Help' },
      { locale: 'de', name: 'Hilfe' },
      { locale: 'fr', name: 'Aide' },
      { locale: 'it', name: 'Aiuto' },
      { locale: 'es', name: 'Ayuda' },
      { locale: 'pt', name: 'Ajuda' },
      { locale: 'bg', name: 'Помощ' },
      { locale: 'cs', name: 'Pomoc' },
      { locale: 'da', name: 'Hjælp' },
      { locale: 'el', name: 'Βοήθεια' },
      { locale: 'et', name: 'Abi' },
      { locale: 'fi', name: 'Auttaa' },
      { locale: 'hu', name: 'Segítség' },
      { locale: 'id', name: 'Membantu' },
      { locale: 'ja', name: 'ヘルプ' },
      { locale: 'ko', name: '돕다' },
      { locale: 'lt', name: 'Pagalba' },
      { locale: 'lv', name: 'Palīdzība' },
      { locale: 'nb', name: 'Hjelp' },
      { locale: 'nl', name: 'Hulp' },
      { locale: 'pl', name: 'Pomoc' },
      { locale: 'pt-br', name: 'Ajuda' },
      { locale: 'ro', name: 'Ajutor' },
      { locale: 'ru', name: 'Помощь' },
      { locale: 'sk', name: 'Pomocník' },
      { locale: 'sl', name: 'Pomoč' },
      { locale: 'sv', name: 'Hjälp' },
      { locale: 'tr', name: 'Yardım' },
      { locale: 'uk', name: 'Допомога' },
      { locale: 'zh', name: '帮助' },
    ],
  },
  mutations: {
    setEditHelpPage(state, payload) {
      state.editHelpPage = payload;
    },
    setHelpPages(state, payload) {
      state.helpPages = payload;
    },
    setNameTranslations(state, payload) {
      state.nameTranslations = payload;
    },
    setSelectedLanguage(state, payload) {
      state.selectedLanguage = payload;
    },
    setContentTranslations(state, payload) {
      state.contentTranslations = payload;
    },
    setRedirectRoute(state, payload) {
      state.redirectRoute = payload;
    },
    setCss(state, payload) {
      state.css = payload;
    },
  },
  actions: {
    prepareTranslations({ commit }, payload) {
      const { translations } = payload;
      const nameTranslations = {};
      const contentTranslations = {};
      translations.forEach((t) => {
        nameTranslations[t.locale] = t.attributes.name;
        contentTranslations[t.locale] = t.attributes.content;
      });
      commit('setNameTranslations', nameTranslations);
      commit('setContentTranslations', contentTranslations);
    },
    getHelpPages({ state }, params) {
      store.dispatch('pagination/paginate', {
        whatToCall: 'helpPages/getHelpPages',
        whereToSave: 'helpPages/setHelpPages',
        url: apiEndpoints.master.helpPages,
        data: state.helpPages,
        params: params,
      });
    },
    setHelpPages({ commit }, payload) {
      commit('setHelpPages', payload);
    },
    getHelpPage({ commit, dispatch }, id) {
      commit('loader/setScreenLoading', true, { root: true });
      const url = `${apiEndpoints.master.helpPages}/${id}`;
      return httpServiceAuth
        .get(url)
        .then((response) => {
          const helpPage = { ...response.data.data };
          commit('setEditHelpPage', helpPage);
          dispatch('prepareTranslations', {
            translations: response.data.data.translations,
          });
          return response.data.data;
        })
        .catch((error) => {
          if (error.response) {
            errorHandler(error.response);
          }
        })
        .finally(() => {
          commit('loader/setScreenLoading', false, { root: true });
        });
    },
    addHelpPage({ commit }, payload) {
      commit('loader/setScreenLoading', true, { root: true });
      const url = `${apiEndpoints.master.helpPages}`;
      return httpServiceAuth
        .post(url, payload)
        .then((response) => {
          return response.data.data;
        })
        .catch((error) => {
          if (error.response) {
            errorHandler(error.response);
          }
        })
        .finally(() => {
          commit('loader/setScreenLoading', false, { root: true });
        });
    },
    updateHelpPage({ commit }, { id, payload }) {
      commit('loader/setScreenLoading', true, { root: true });
      const url = `${apiEndpoints.master.helpPages}/${id}`;
      return httpServiceAuth
        .put(url, payload)
        .then((response) => {
          commit('setEditHelpPage', response.data.data);
          return response.data.data;
        })
        .catch((error) => {
          if (error.response) {
            errorHandler(error.response);
          }
        })
        .finally(() => {
          commit('loader/setScreenLoading', false, { root: true });
        });
    },
    deleteHelpPage({ commit }, payload) {
      commit('loader/setScreenLoading', true, { root: true });
      const url = `${apiEndpoints.master.helpPages}/${payload}`;
      return httpServiceAuth
        .delete(url)
        .catch((error) => {
          if (error.response) {
            errorHandler(error.response);
          }
        })
        .finally(() => {
          commit('loader/setScreenLoading', false, { root: true });
        });
    },
    setRedirectRoute({ commit }, payload) {
      commit('setRedirectRoute', payload);
    },
    setCss({ commit }, payload) {
      commit('setCss', payload);
    },
    getCss({ commit }) {
      commit('loader/setScreenLoading', true, { root: true });
      const url = `${apiEndpoints.master.helpCss}`;
      return httpServiceAuth
        .get(url)
        .then((response) => {
          const css = response.data.data.content;
          commit('setCss', css);
        })
        .catch((error) => {
          if (error.response) {
            errorHandler(error.response);
          }
        })
        .finally(() => {
          commit('loader/setScreenLoading', false, { root: true });
        });
    },
    updateCss({ commit }, payload) {
      commit('loader/setScreenLoading', true, { root: true });
      const url = `${apiEndpoints.master.helpCss}/1`;
      return httpServiceAuth
        .put(url, payload)
        .then((response) => {
          return response.data.data;
        })
        .catch((error) => {
          if (error.response) {
            errorHandler(error.response);
          }
        })
        .finally(() => {
          commit('loader/setScreenLoading', false, { root: true });
        });
    },
  },
};
